/**
 * Fusion canvas — its OWN Cytoscape instance (window.__fusionCy), own seed, own
 * stylesheet. Imports nothing from the crypto Workbench canvas stack, so it
 * cannot regress it.
 *
 * STABLE-CAMERA model (fixes the "view keeps zooming / nodes keep growing" bug):
 *   - Positions are computed ONCE at mount by a non-animated ELK `layered` pass
 *     over the full element set (see fusionLayout.runLayout). ELK is asynchronous:
 *     positions land on `layoutstop`, and the single initial fit waits for it.
 *   - Placement follows declared phase lanes (macro view, fusionMacro.ts) or,
 *     without them, declared `placement_role` columns (R24); the layout owns
 *     coordinates.
 *   - Replay then only toggles VISIBILITY classes (.cs-stephidden) per stage —
 *     nodes fade in where they belong; the camera does NOT move and nothing
 *     re-lays-out. The replay emphasis (cs-focus-current / cs-dimmed-past) is
 *     applied only while a demo replay or stage scrub is in progress — never at
 *     rest and never on a real case, so role styling is not repainted.
 *   - The camera only reframes on user actions (module filter, Fit, Re-layout).
 */
import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import { CyZoomControls } from '../../case-canvas/viewport/CyZoomControls';
import { attachViewportKeys, enableViewport } from '../../case-canvas/viewport/cyViewport';
import cytoscape from 'cytoscape';
import '../../utils/workerElkLayout';
import type { ElementDefinition } from 'cytoscape';
import { useNavigate } from 'react-router-dom';

import { fusionStylesheet, type FusionModule } from '../../types/fusion';
import { toCyElements, MAX_STEP, USER_MAX_STEP } from '../../data/fusionSeed';
import { useFusionStore } from '../../stores/useFusionStore';
import { useIntakeStore } from '../../stores/useIntakeStore';
import { usePlaybackTimer } from '../../hooks/usePlaybackTimer';
import { runLayout, fitTo, visibleEles, fitElements, applyZoomBounds, bloom, layoutSettled, MAX_ZOOM, DEFAULT_MIN_ZOOM } from './fusionLayout';
import { contextChipLabel, partitionRoleFirst, toggleContextHold, type RoleFirstPlan } from './roleFirstCanvas';
import { applyViewState, captureCamera, readViewState, writeViewState, type ViewLevel } from '../../case-canvas/viewState';
import { loadFusionCase } from '../../case-canvas/fusionCaseStore';
import { linkToSubject, linkDerivedIdentifiers, setSubjectName, subjectEdgeLabel, SUBJECT_HUB_ID } from '../../case-canvas/entityJoin';
import { openWalletNodeInWorkbench } from './workbenchLink';
import { expandableMembers, collapseGroupBoxes, declarePlacementRoles, groupMemberRows, resolveReveal } from './fusionMacro';
import { GroupMemberList } from './GroupMemberList';
import { onReveal, takePendingReveals } from './fusionReveal';
import { relatedCaseMarks, type CaseLinkRow } from '../../case-canvas/relatedCases';
import { nodeHoverTitle } from '../../services/caseCanvas';

// The synthetic demo seed, stamped with `demo: 1` so it can be told apart from
// user-added data (canonical `ent:` / `sf:` / `intake:` nodes) at any time —
// including inside saved cases — and removed when "Demo data" is toggled off.
const DEFAULT_ELEMENTS: ElementDefinition[] = toCyElements().map((el) => ({
  ...el, data: { ...(el as { data: Record<string, unknown> }).data, demo: 1 },
}));
// Seed ids as a fallback marker for saved cases written before the demo stamp existed.
const SEED_IDS = new Set(DEFAULT_ELEMENTS.map((el) => String((el.data as { id?: string }).id)));
const isDemoEl = (data: Record<string, unknown>): boolean =>
  data.demo === 1 || SEED_IDS.has(String(data.id));
const withoutDemo = (els: ElementDefinition[]): ElementDefinition[] =>
  els.filter((el) => {
    const d = el.data as Record<string, unknown>;
    // drop demo elements AND edges touching them (their endpoint is going away)
    if (isDemoEl(d)) return false;
    if (d.source && (SEED_IDS.has(String(d.source)) || SEED_IDS.has(String(d.target)))) return false;
    return true;
  });
const STYLE = fusionStylesheet();

// ── pure class gating (no layout, no camera) ──
/**
 * Stage gating. `focus.active` is false on a real case (every node is step 1)
 * and at rest; emphasis classes are added only mid-replay (step < maxStep).
 */
function gateStep(cy: cytoscape.Core, step: number, focus: { maxStep: number; active: boolean }) {
  cy.batch(() => cy.elements().forEach((el) => {
    const elStep = Number(el.data('step')) || 1;
    el.removeClass('cs-stephidden cs-dimmed-past cs-focus-current');
    if (elStep > step) {
      el.addClass('cs-stephidden');
      return;
    }
    if (!focus.active || step >= focus.maxStep) return;   // at rest: no emphasis
    if (elStep < step) el.addClass('cs-dimmed-past');
    else if (el.isNode()) el.addClass('cs-focus-current');
  }));
}
function gateCourtSafe(cy: cytoscape.Core, courtSafe: boolean) {
  cy.batch(() => {
    cy.elements().removeClass('cs-dim');
    if (!courtSafe) return;
    cy.edges('[determinism="scored"]').addClass('cs-dim');
    cy.nodes().forEach((n) => {
      const edges = n.connectedEdges().not('.cs-stephidden');
      if (edges.length > 0 && edges.every((e) => e.data('determinism') === 'scored')) n.addClass('cs-dim');
    });
  });
}
function gateModules(cy: cytoscape.Core, filters: Record<FusionModule, boolean>) {
  cy.batch(() => {
    cy.elements().removeClass('cs-modhidden');
    (Object.keys(filters) as FusionModule[]).forEach((m) => {
      if (!filters[m]) {
        const nodes = cy.nodes(`[module="${m}"]`);
        nodes.addClass('cs-modhidden');
        nodes.connectedEdges().addClass('cs-modhidden');
      }
    });
  });
}

interface FusionCanvasProps {
  elements?: ElementDefinition[];
  caseId?: string;
  /** Altitude of `elements`; a remembered camera only applies at the level it was taken at. */
  graphLevel?: ViewLevel;
  /** Macro view: expand a collapsed cluster or group box (its member ids) into the detail view. */
  onExpandCluster?: (memberIds: string[], clusterId: string) => void;
  /** Identifiers (node id, canonical id, address) that must be on the canvas: the boxes holding them open. */
  revealKeys?: ReadonlySet<string>;
  /** Cases linked to this one (NS-58): nodes whose canonical id is shared with a link get a related-case marker. */
  relatedCases?: readonly CaseLinkRow[];
  /** Test override for FUSION_CANVAS_ROLE_FIRST_MIN_NODES. Production reads the setting. */
  roleFirstMinNodes?: number;
  /** Test override for FUSION_CANVAS_HARD_CAP. */
  roleFirstHardCap?: number;
  /** OPEN01: the graph is the spine and `count` context nodes were left on the server; the chip asks for them. */
  remoteContext?: { count: number; onLoad: () => void };
  /** OPEN01: the officer asked for the context set; the whole graph is laid out, no role-first split. */
  showAllContext?: boolean;
}

/**
 * A real case opens with every group box folded into one card (name, member count, total by token);
 * a double-click on a card opens that box. Which boxes are open is part of the per-case view state
 * (viewState.expanded), so it survives a return. Opening a box changes the graph, so the canvas is
 * rebuilt (one layout) and framed on the opened box. Anything that needs a node inside a folded box
 * calls requestReveal(ids) (fusionReveal.ts). The sandbox (no elements) is passed through unchanged.
 */
export function FusionCanvas(props: FusionCanvasProps = {}) {
  const { elements, caseId, revealKeys, roleFirstMinNodes, roleFirstHardCap, showAllContext } = props;
  const rememberCase = elements && caseId ? caseId : undefined;
  // Boxes to open / members to pin because something asked for them before this canvas mounted.
  const [initialReveal] = useState(() => (elements
    ? resolveReveal(elements, [...(revealKeys ?? []), ...takePendingReveals()])
    : { open: [] as string[], pin: [] as string[] }));
  const [expanded, setExpanded] = useState<string[]>(() => {
    const stored = rememberCase ? readViewState(rememberCase)?.expanded ?? [] : [];
    return [...new Set([...stored, ...initialReveal.open])].slice(-24);
  });
  const [focusBoxes, setFocusBoxes] = useState<string[]>([]);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  // Members of a big group (more than BIG_GROUP_MIN) the officer pinned onto the canvas; memory only.
  const [pinned, setPinned] = useState<string[]>(initialReveal.pin);
  const [listBox, setListBox] = useState<string | null>(null);
  const collapsed = useMemo(
    () => (elements ? collapseGroupBoxes(elements, new Set(expanded), new Set(pinned)) : undefined),
    [elements, expanded, pinned],
  );
  // Context leaves the canvas before roles are declared on the set ELK will see.
  const contextPlan = useMemo(
    () => (collapsed && !showAllContext ? partitionRoleFirst(collapsed, { minNodes: roleFirstMinNodes, hardCap: roleFirstHardCap }) : null),
    [collapsed, roleFirstMinNodes, roleFirstHardCap, showAllContext],
  );
  const shown = useMemo(
    () => (contextPlan ? declarePlacementRoles(contextPlan.layout) : undefined),
    [contextPlan],
  );
  const bigBoxes = useMemo(() => {
    const out = new Set<string>();
    (shown || []).forEach((el) => { const d = el.data as Record<string, unknown>; if (d.big_group === true) out.add(String(d.id)); });
    return out;
  }, [shown]);
  const listTitle = useMemo(() => {
    const card = (shown || []).find((e) => (e.data as { id?: string }).id === listBox);
    return String((card?.data as { label?: string } | undefined)?.label || listBox || '').split('\n')[0];
  }, [shown, listBox]);
  const pinMember = useCallback((id: string) => setPinned((p) => (p.includes(id) ? p : [...p, id].slice(-60))), []);
  const openBoxes = useCallback((boxIds: string[]) => {
    const have = new Set(expandedRef.current);
    const fresh = boxIds.filter((b) => !have.has(b));
    if (!fresh.length) return;
    const next = [...expandedRef.current, ...fresh].slice(-24);
    if (rememberCase) writeViewState(rememberCase, { expanded: next });
    setFocusBoxes(fresh);
    setExpanded(next);
  }, [rememberCase]);
  // A node that must be on the canvas: open the small boxes that hold it, pin it out of a big group.
  const revealRef = useRef<(ids: string[]) => void>(() => {});
  revealRef.current = (ids) => {
    if (!elements) return;
    const { open, pin } = resolveReveal(elements, ids);
    openBoxes(open);
    pin.forEach(pinMember);
  };
  useEffect(() => {
    if (!elements) return undefined;
    return onReveal((ids) => revealRef.current(ids));
  }, [elements]);
  useEffect(() => {
    if (elements && revealKeys?.size) revealRef.current([...revealKeys]);
  }, [elements, revealKeys]);
  return (
    <>
      <FusionCanvasView
        key={`${expanded.join('\n')}#${pinned.join('\n')}`}
        {...props}
        elements={shown}
        contextPlan={contextPlan && contextPlan.mode !== 'passthrough' ? contextPlan : null}
        focusBoxes={focusBoxes}
        onExpandBox={elements ? (id) => (bigBoxes.has(id) ? setListBox(id) : openBoxes([id])) : undefined}
      />
      {elements && listBox && bigBoxes.has(listBox) && (
        <GroupMemberList
          title={listTitle}
          rows={groupMemberRows(elements, listBox)}
          pinned={new Set(pinned)}
          onPin={pinMember}
          onClose={() => setListBox(null)}
        />
      )}
    </>
  );
}

function FusionCanvasView({ elements, caseId, graphLevel = 'low', onExpandCluster, focusBoxes, onExpandBox, relatedCases, contextPlan, remoteContext }: FusionCanvasProps & {
  /** Boxes opened by the last expand: the frame goes to them instead of the whole graph. */
  focusBoxes?: string[];
  /** A folded card was double-clicked: open its box. */
  onExpandBox?: (boxId: string) => void;
  /** Context held off the canvas. Null when the graph is under the role-first threshold. */
  contextPlan?: RoleFirstPlan | null;
}) {
  const navigate = useNavigate();
  const expandBoxRef = useRef(onExpandBox);
  expandBoxRef.current = onExpandBox;
  const focusRef = useRef(focusBoxes);
  focusRef.current = focusBoxes;
  // Latest callback without re-binding cytoscape handlers (onCy runs once per instance).
  const expandRef = useRef(onExpandCluster);
  expandRef.current = onExpandCluster;
  // Per-case view memory (camera, selection) applies to a real case only, never to the sandbox.
  const rememberCase = elements && caseId ? caseId : undefined;
  // Saving starts only after the remembered view was applied, so a half-restored canvas never overwrites it.
  const persistOn = useRef(false);
  const saveTimer = useRef<number | undefined>(undefined);
  // Replay emphasis exists only on the demo/sandbox board, never on a real case.
  const focusFor = (showDemoNow: boolean) => ({
    maxStep: showDemoNow ? MAX_STEP : USER_MAX_STEP,
    active: !elements,
  });
  // Demo only (no `elements` prop): prefer a saved Fusion case so the canvas survives reloads
  // + tab switches; else the pristine seed. A real case always passes its projected `elements`.
  const [saved] = useState<ElementDefinition[] | null>(() => (elements ? null : loadFusionCase()?.elements ?? null));
  const showDemo = useFusionStore((s) => s.showDemo);
  // The elements prop MUST be reference-stable: react-cytoscapejs reconciles the
  // canvas to whatever array it receives, so recomputing this on a showDemo flip
  // would wipe the whole graph (user data included). Compute it ONCE at mount
  // (showDemo read here is the initial value); the demo seed is then added/removed
  // imperatively by the toggle effect below, and user data is never in this array.
  const [ELEMENTS] = useState<ElementDefinition[]>(() =>
    elements ?? (showDemo ? (saved ?? DEFAULT_ELEMENTS) : withoutDemo(saved ?? [])));
  const cyRef = useRef<cytoscape.Core | null>(null);
  // Same instance as cyRef, as state so the zoom controls and minimap re-render once it exists.
  const [cyInst, setCyInst] = useState<cytoscape.Core | null>(null);
  const [contextShown, setContextShown] = useState(false);
  const heldRef = useRef(contextPlan?.held ?? []);
  heldRef.current = contextPlan?.held ?? [];
  const showContext = useCallback(() => {
    const cy = cyRef.current;
    if (!cy || cy.destroyed() || contextPlan?.mode !== 'role-first') return;
    const next = toggleContextHold(cy, heldRef.current, contextShown, (inst) => {
      runLayout(inst, inst.elements());
    });
    setContextShown(next);
  }, [contextPlan?.mode, contextShown]);
  const select = useFusionStore((s) => s.select);
  const courtSafe = useFusionStore((s) => s.courtSafe);
  const moduleFilters = useFusionStore((s) => s.moduleFilters);
  const step = useFusionStore((s) => s.step);
  const playing = useFusionStore((s) => s.playing);
  const setStep = useFusionStore((s) => s.setStep);
  const setPlaying = useFusionStore((s) => s.setPlaying);

  const onCy = (cy: cytoscape.Core) => {
    if (cyRef.current === cy) return;
    cyRef.current = cy;
    setCyInst(cy);
    (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy = cy;
    cy.on('tap', 'node', (ev) => {
      // Phase lanes are layout structure, not evidence: never an Inspector subject.
      if (ev.target.data('type') === 'phase_lane') { select(null); return; }
      select({ id: ev.target.id(), kind: 'node' });
    });
    cy.on('tap', 'edge', (ev) => select({ id: ev.target.id(), kind: 'edge' }));
    // Edge labels are hidden when zoomed out (stylesheet min-zoomed-font-size); hovering an edge shows its label.
    cy.on('mouseover', 'edge', (ev) => ev.target.addClass('cs-label-hover'));
    cy.on('mouseout', 'edge', (ev) => ev.target.removeClass('cs-label-hover'));
    // A node that also appears in a linked case names that case on hover (native tooltip on the canvas); a dormant
    // node (RET01) says it was seen only in earlier runs, and since which run.
    cy.on('mouseover', 'node[?related_case], node[?dormant]', (ev) => {
      const box = cy.container();
      if (box) box.title = nodeHoverTitle(ev.target.data() as Record<string, unknown>);
    });
    cy.on('mouseout', 'node[?related_case], node[?dormant]', () => { const box = cy.container(); if (box) box.removeAttribute('title'); });
    cy.on('tap', (ev) => { if (ev.target === cy) { select(null); useFusionStore.getState().closeRadial(); } });
    // Right-click a node → operator radial (Case Canvas). Suppress the OS menu on the canvas
    // in capture phase so it never races the radial open (same bug as Workbench GraphCanvas).
    cy.container()?.addEventListener('contextmenu', (e) => e.preventDefault(), true);
    cy.on('cxttap', 'node', (ev) => {
      const oe = ev.originalEvent as MouseEvent | undefined;
      useFusionStore.getState().openRadial({
        x: oe?.clientX ?? window.innerWidth / 2,
        y: oe?.clientY ?? window.innerHeight / 2,
        nodeId: ev.target.id(),
        nodeType: String(ev.target.data('type') || ''),
        module: String(ev.target.data('module') || ''),
      });
    });
    cy.on('dbltap', 'node', (ev) => {
      const data = ev.target.data() as Record<string, unknown>;
      // A folded group card opens in place (the canvas is rebuilt with that box expanded).
      if (data.collapsed === true && expandBoxRef.current) {
        expandBoxRef.current(String(data.id));
        return;
      }
      // Macro view: a collapsed cluster or a group box expands into the detail view.
      const members = expandableMembers(data);
      if (members.length && expandRef.current) {
        expandRef.current(members, String(data.id));
        return;
      }
      if (data.type === 'phase_lane' || data.type === 'group_box') return;
      openWalletNodeInWorkbench(data, caseId, navigate);
    });
    cy.minZoom(DEFAULT_MIN_ZOOM); cy.maxZoom(MAX_ZOOM);   // minZoom is re-derived from the graph once it is laid out (applyZoomBounds)
    // Wheel zoom about the pointer, drag pan and touch pinch are Cytoscape built-ins; pin them on
    // and add keyboard +/-/0 while the canvas has focus (controls render below).
    enableViewport(cy);
    attachViewportKeys(cy, fitElements);

    // Restore the user's own elements captured on the last unmount so "my data"
    // survives tab switches (this canvas mounts a fresh instance each time). Nodes
    // before edges so an edge never lands before its endpoints. Dedup by id.
    // SANDBOX ONLY: `userElements` is the /fusion sandbox's own board. A real case (it passes its
    // projected `elements` and a case id) must never receive it.
    const restore = !elements && !caseId ? useIntakeStore.getState().userElements : [];
    if (restore.length) {
      restore.filter((d) => !d.source).forEach((data) => { if (cy.getElementById(String(data.id)).empty()) { try { cy.add({ data }); } catch { /* skip */ } } });
      restore.filter((d) => d.source).forEach((data) => { if (cy.getElementById(String(data.id)).empty()) { try { cy.add({ data }); } catch { /* skip */ } } });
    }
    // Drain any nodes/edges produced while the canvas was unmounted (authored on the Build tab).
    // Nodes first so edges never land before their endpoints.
    const pending = useIntakeStore.getState().drainPendingNodes();
    const pendingNodes = pending.filter((d) => !d.source);
    const pendingEdges = pending.filter((d) => d.source);
    pendingNodes.forEach((data) => { try { if (cy.getElementById(String(data.id)).empty()) cy.add({ data }); } catch { /* dup id / bad data — skip */ } });
    pendingEdges.forEach((data) => { try { if (cy.getElementById(String(data.id)).empty()) cy.add({ data }); } catch { /* skip */ } });
    // Tie the drained user identifiers into the subject hub + derived joins so a
    // board authored entirely on the Build tab lands connected on first Canvas view.
    if (pending.length) {
      pendingNodes.forEach((data) => {
        if (String(data.id).startsWith('ent:') && data.type !== 'identity_hub') linkToSubject(cy, String(data.id));
      });
      linkDerivedIdentifiers(cy);
    }
    // Re-apply the case subject's name to the hub if the officer set one (survives
    // tab switches — the hub node itself is restored above, but its label follows
    // the store so a rename on the Build tab reflects here).
    const subjName = useIntakeStore.getState().subjectName;
    if (subjName && subjName.trim() && cy.getElementById(SUBJECT_HUB_ID).nonempty()) {
      setSubjectName(cy, subjName);
    }
    // Refresh subject-edge labels on restored boards so each reads what the
    // identifier IS ("phone number" / "UPI ID" / "bank account"), not the old
    // generic "subject identifier".
    cy.edges().forEach((e) => {
      if (e.id().startsWith('subj:')) e.data('_edgeLabel', subjectEdgeLabel(cy, String(e.data('target'))));
    });
    // Position the WHOLE board once, frame it once — the camera is now fixed. A restored saved
    // case already carries positions → keep them (only re-layout the pristine seed, or when new
    // queued nodes need placing) and show the full board (past the ingestion replay).
    const st = useFusionStore.getState();
    // Effective max stage: the demo walks 5 stages; a "my data only" board walks
    // the 3 user surfaces (identifiers → account → counterparties).
    const effMax = st.showDemo ? MAX_STEP : USER_MAX_STEP;
    if (!saved || pending.length || restore.length) runLayout(cy, cy.elements());
    if (saved && st.step !== effMax) st.setStep(effMax);
    const gateAt = saved ? effMax : Math.min(st.step, effMax);
    gateStep(cy, gateAt, focusFor(st.showDemo));
    gateModules(cy, st.moduleFilters);
    gateCourtSafe(cy, st.courtSafe);
    // Real case: re-apply the remembered camera and selection once ELK has placed the nodes; fit
    // when nothing is remembered, the graph or level changed, or the remembered node is gone.
    const remembered = rememberCase ? readViewState(rememberCase) : null;
    if (rememberCase) {
      void layoutSettled(cy).then(() => {
        if (cy.destroyed()) return;
        applyZoomBounds(cy);   // before the remembered camera: a zoom below 0.3 must not be clamped
        const r = applyViewState(cy, remembered, graphLevel);
        if (!r.camera) {
          // After opening a box, frame that box; otherwise the whole graph.
          const opened = cy.collection();
          (focusRef.current || []).forEach((id) => {
            const box = cy.getElementById(id);
            if (box.nonempty()) { opened.merge(box); opened.merge(box.descendants()); }
          });
          fitTo(cy, opened.nonempty() ? opened : cy.elements(), 60, 0);
        }
        if (r.selected) {
          cy.elements().unselect();
          cy.getElementById(r.selected.id).select();
          useFusionStore.getState().select(r.selected);
        } else if (remembered?.selected) {
          writeViewState(rememberCase, { selected: null });
        }
        persistOn.current = true;
        cy.on('viewport', () => {
          if (!persistOn.current) return;
          window.clearTimeout(saveTimer.current);
          saveTimer.current = window.setTimeout(() => {
            if (!cy.destroyed()) writeViewState(rememberCase, captureCamera(cy, graphLevel));
          }, 300);
        });
      });
    } else {
      fitTo(cy, cy.elements(), 60, 0);   // one instant frame of the full board, after ELK places it
    }
    if (pendingNodes.length) bloom(cy, pendingNodes.map((d) => String(d.id)));
  };

  // Remember the selection per case (real case only, once the remembered view is applied).
  useEffect(() => {
    if (!rememberCase) return;
    const unsub = useFusionStore.subscribe((s, prev) => {
      if (!persistOn.current || s.selected === prev.selected) return;
      writeViewState(rememberCase, { selected: s.selected ?? null });
    });
    return () => { unsub(); window.clearTimeout(saveTimer.current); };
  }, [rememberCase]);

  // Stage change → toggle visibility only. No layout, no camera move (stable camera).
  useEffect(() => {
    const cy = cyRef.current; if (!cy) return;
    const st = useFusionStore.getState();
    gateStep(cy, step, focusFor(st.showDemo));
    gateModules(cy, st.moduleFilters);
    gateCourtSafe(cy, st.courtSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => { if (cyRef.current) gateCourtSafe(cyRef.current, courtSafe); }, [courtSafe]);

  // Related-case markers: DATA only (the stylesheet draws the ring), so no layout runs and the camera stays.
  useEffect(() => {
    const cy = cyInst; if (!cy || cy.destroyed?.()) return;
    const marks = relatedCaseMarks(
      cy.nodes().map((n) => ({ id: n.id(), data: n.data() as Record<string, unknown> })), relatedCases ?? []);
    cy.batch(() => cy.nodes().forEach((n) => {
      const label = marks.get(n.id());
      if (label) { n.data('related_case', true); n.data('related_case_label', label); }
      else if (n.data('related_case')) { n.removeData('related_case related_case_label'); }
    }));
  }, [cyInst, relatedCases, ELEMENTS]);

  // Module filter is a user action → reposition the remaining nodes + reframe.
  // The mount pass is skipped: onCy already gated, laid out and framed (or restored the remembered
  // view); a second layout + fit here would override that camera.
  const filtersSeen = useRef(moduleFilters);
  useEffect(() => {
    const cy = cyRef.current; if (!cy) return;
    if (filtersSeen.current === moduleFilters) return;
    filtersSeen.current = moduleFilters;
    gateModules(cy, moduleFilters);
    runLayout(cy, visibleEles(cy));
    fitTo(cy, visibleEles(cy), 55);
  }, [moduleFilters]);

  // "Demo data" toggle is a user action → add/remove the synthetic seed in place,
  // leaving the user's own (ent:/sf:/intake:) elements untouched. Demo surface only.
  const demoRef = useRef(showDemo);
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || elements) { demoRef.current = showDemo; return; }
    if (demoRef.current === showDemo) return;   // mount already seeded correctly
    demoRef.current = showDemo;
    if (!showDemo) {
      cy.remove(cy.elements().filter((el) => isDemoEl(el.data() as Record<string, unknown>)));
      // Re-gate within the user's 3 surface-stages so hiding the demo doesn't
      // leave the board stuck at a demo stage index.
      gateStep(cy, Math.min(useFusionStore.getState().step, USER_MAX_STEP), focusFor(false));
    } else {
      // nodes first, then edges — an edge can't land before its endpoints
      const missing = DEFAULT_ELEMENTS.filter((el) => cy.getElementById(String((el.data as { id?: string }).id)).empty());
      missing.filter((el) => !(el.data as { source?: string }).source).forEach((el) => { try { cy.add(el); } catch { /* skip */ } });
      missing.filter((el) => (el.data as { source?: string }).source).forEach((el) => { try { cy.add(el); } catch { /* skip */ } });
      const st = useFusionStore.getState();
      gateStep(cy, st.step, focusFor(true)); gateModules(cy, st.moduleFilters); gateCourtSafe(cy, st.courtSafe);
    }
    runLayout(cy, visibleEles(cy));
    fitTo(cy, visibleEles(cy), 55);
  }, [showDemo, elements]);

  // Replay timer (generic hook) — advances the stage; visibility effect does the rest.
  // Stop at the demo max (5) or the user max (3) depending on which board is shown.
  const advance = useCallback((n: number) => setStep(n), [setStep]);
  const stop = useCallback(() => setPlaying(false), [setPlaying]);
  const effMaxStep = showDemo ? MAX_STEP : USER_MAX_STEP;
  usePlaybackTimer(playing, step, effMaxStep, advance, stop, 1300);

  // On unmount (tab switch), (1) snapshot the user's own elements so they can be
  // restored on the next mount — the demo canvas builds a fresh instance each time,
  // so without this "my data" would vanish on a tab switch; (2) don't leave
  // window.__fusionCy pointing at a destroyed instance (QA F-1/F-2).
  useEffect(() => () => {
    const cy = cyRef.current;
    if (rememberCase && persistOn.current && cy && !cy.destroyed()) writeViewState(rememberCase, captureCamera(cy, graphLevel));
    if (cy && !cy.destroyed() && !elements) {
      const user = cy.elements()
        .filter((el) => !isDemoEl(el.data() as Record<string, unknown>))
        .map((el) => ({ ...(el.data() as Record<string, unknown>) }) as { id: string });
      useIntakeStore.getState().saveUserElements(user);
    }
    const w = window as unknown as { __fusionCy?: cytoscape.Core };
    if (w.__fusionCy === cyRef.current) w.__fusionCy = undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  const chip = contextPlan ? contextChipLabel(contextPlan, contextShown) : null;
  return (
    <div className="relative w-full h-full min-h-0">
      <CytoscapeComponent
        elements={ELEMENTS as any}
        stylesheet={STYLE as any}
        cy={onCy}
        style={{ width: '100%', height: '100%', background: 'transparent', position: 'relative', zIndex: 1 }}
      />
      {remoteContext && (
        <button
          type="button"
          data-testid="fusion-context-chip"
          data-mode="remote"
          onClick={remoteContext.onLoad}
          className="absolute top-2 left-2 z-20 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground shadow-overlay hover:bg-secondary"
        >
          {`context: ${remoteContext.count.toLocaleString('en-US')} hidden (load)`}
        </button>
      )}
      {chip && contextPlan?.mode === 'hard-cap' && !remoteContext && (
        <p
          data-testid="fusion-context-chip"
          data-mode="hard-cap"
          className="absolute top-2 left-2 z-20 max-w-md rounded-md border border-border bg-card px-2.5 py-1 text-xs text-foreground shadow-overlay"
        >
          {chip}
        </p>
      )}
      {chip && contextPlan?.mode === 'role-first' && !remoteContext && (
        <button
          type="button"
          data-testid="fusion-context-chip"
          data-mode="role-first"
          aria-pressed={contextShown}
          onClick={showContext}
          className="absolute top-2 left-2 z-20 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground shadow-overlay hover:bg-secondary"
        >
          {chip}
        </button>
      )}
      <CyZoomControls cy={cyInst} getEles={fitElements} />
    </div>
  );
}
